import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { 
    Settings, User, Bell, Palette, Moon, Sun, Monitor, Loader2, LogOut, 
    Crown, Camera, Trash2, Download, FileText, Mail, Check, Sparkles
} from 'lucide-react';

const SettingsPage = () => {
    const { user, api, logout, updatePreferences, refreshUser, checkFeature } = useAuth();
    const { theme, setTheme } = useTheme();
    const [searchParams, setSearchParams] = useSearchParams();
    const [receiveEmails, setReceiveEmails] = useState(user?.receive_emails ?? true);
    const [saving, setSaving] = useState(false);
    const [preferences, setPreferences] = useState(user?.theme_preferences || {});
    const [profilePicture, setProfilePicture] = useState(null);
    const [uploadingPicture, setUploadingPicture] = useState(false);
    const [exportingPdf, setExportingPdf] = useState(false);
    const fileInputRef = useRef(null);

    const canExportPdf = checkFeature ? checkFeature('journal_export') : false;

    useEffect(() => {
        checkPaymentStatus();
        fetchProfilePicture();
    }, []);

    const fetchProfilePicture = async () => {
        try {
            const response = await api.get('/auth/profile-picture');
            setProfilePicture(response.data.profile_picture);
        } catch (error) {
            console.error('Failed to fetch profile picture:', error);
        }
    };

    const handleProfilePictureUpload = async (event) => {
        const file = event.target.files?.[0];
        if (!file) return;

        if (!file.type.startsWith('image/')) {
            toast.error('Please select an image file');
            return;
        }

        if (file.size > 5 * 1024 * 1024) {
            toast.error('Image must be less than 5MB');
            return;
        }

        setUploadingPicture(true);
        try {
            const reader = new FileReader();
            reader.onloadend = async () => {
                const base64Data = reader.result;
                await api.post('/auth/upload-profile-picture', { image_data: base64Data });
                setProfilePicture(base64Data);
                toast.success('Profile picture uploaded!');
                if (refreshUser) await refreshUser();
            };
            reader.readAsDataURL(file);
        } catch (error) {
            toast.error('Failed to upload profile picture');
        } finally {
            setUploadingPicture(false);
        }
    };

    const handleDeleteProfilePicture = async () => {
        try {
            await api.delete('/auth/profile-picture');
            setProfilePicture(null);
            toast.success('Profile picture removed');
            if (refreshUser) await refreshUser();
        } catch (error) {
            toast.error('Failed to remove profile picture');
        }
    };

    const handleExportPdf = async (days = 30) => {
        if (!canExportPdf) {
            toast.error('PDF export requires Plus subscription');
            return;
        }

        setExportingPdf(true);
        try {
            const response = await api.get(`/journal/export/pdf?days=${days}`, {
                responseType: 'blob'
            });
            
            const blob = new Blob([response.data], { type: 'application/pdf' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `moodtrack_journal_${new Date().toISOString().split('T')[0]}.pdf`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
            
            toast.success('Journal exported successfully!');
        } catch (error) {
            toast.error('Failed to export journal');
        } finally {
            setExportingPdf(false);
        }
    };

    const checkPaymentStatus = async () => {
        const sessionId = searchParams.get('session_id');
        const payment = searchParams.get('payment');

        if (sessionId) {
            // Poll for payment status
            pollPaymentStatus(sessionId);
        } else if (payment === 'success') {
            toast.success('Payment successful! Your subscription has been upgraded.');
            setSearchParams({});
            if (refreshUser) await refreshUser();
        }
    };

    const pollPaymentStatus = async (sessionId, attempts = 0) => {
        if (attempts >= 5) {
            setSearchParams({});
            return;
        }

        try {
            const response = await api.get(`/payments/status/${sessionId}`);
            if (response.data.payment_status === 'paid') {
                toast.success('Payment successful! Your subscription has been upgraded.');
                setSearchParams({});
                if (refreshUser) await refreshUser();
                else window.location.reload();
            } else if (response.data.status !== 'expired') {
                setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
            }
        } catch (error) {
            console.error('Error checking payment:', error);
        }
    };

    const handleEmailToggle = async (value) => {
        setSaving(true);
        try {
            await api.put(`/auth/email-opt-out?receive_emails=${value}`);
            setReceiveEmails(value);
            toast.success(value ? 'Email notifications enabled' : 'Email notifications disabled');
        } catch (error) {
            toast.error('Failed to update preference');
        } finally {
            setSaving(false);
        }
    };

    const handleSavePreferences = async () => {
        setSaving(true);
        try {
            await updatePreferences(preferences);
            toast.success('Preferences saved!');
        } catch (error) {
            toast.error('Failed to save preferences');
        } finally {
            setSaving(false);
        }
    };

    const handleLogout = () => {
        logout();
        window.location.href = '/';
    };

    const themeOptions = [
        { value: 'light', icon: Sun, label: 'Light' },
        { value: 'dark', icon: Moon, label: 'Dark' },
        { value: 'system', icon: Monitor, label: 'System' }
    ];

    return (
        <DashboardLayout>
            <div data-testid="settings-page" className="max-w-3xl mx-auto space-y-6">
                <h1 className="font-heading text-3xl font-semibold text-foreground">
                    Settings
                </h1>

                {/* Profile Section */}
                <Card className="glass-card">
                    <CardHeader>
                        <CardTitle className="font-heading text-xl flex items-center gap-2">
                            <User className="w-5 h-5" />
                            Profile
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-6">
                        {/* Profile Picture and Info */}
                        <div className="flex flex-col sm:flex-row items-center gap-6">
                            {/* Profile Picture */}
                            <div className="relative">
                                <div className="w-24 h-24 rounded-2xl overflow-hidden bg-primary/10 flex items-center justify-center">
                                    {profilePicture ? (
                                        <img 
                                            src={profilePicture} 
                                            alt="Profile" 
                                            className="w-full h-full object-cover"
                                        />
                                    ) : (
                                        <span className="text-3xl font-semibold text-primary">
                                            {user?.name?.charAt(0).toUpperCase()}
                                        </span>
                                    )}
                                </div>
                                {profilePicture && (
                                    <button
                                        onClick={handleDeleteProfilePicture}
                                        className="absolute -top-2 -right-2 w-6 h-6 bg-destructive rounded-full flex items-center justify-center text-destructive-foreground hover:bg-destructive/90"
                                        title="Remove photo"
                                    >
                                        <Trash2 className="w-3 h-3" />
                                    </button>
                                )}
                            </div>

                            {/* User Info and Actions */}
                            <div className="flex flex-col gap-2">
                                <p className="font-medium text-lg">{user?.name}</p>
                                <p className="text-sm text-muted-foreground">{user?.email}</p>
                                
                                <div className="flex flex-wrap gap-2 mt-2">
                                    <input
                                        type="file"
                                        ref={fileInputRef}
                                        onChange={handleProfilePictureUpload}
                                        accept="image/*"
                                        className="hidden"
                                    />
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => fileInputRef.current?.click()}
                                        disabled={uploadingPicture}
                                        className="rounded-full"
                                        data-testid="upload-photo-btn"
                                    >
                                        {uploadingPicture ? (
                                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                        ) : (
                                            <Camera className="w-4 h-4 mr-2" />
                                        )}
                                        Upload Photo
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {/* PDF Export Section */}
                <Card className="glass-card">
                    <CardHeader>
                        <CardTitle className="font-heading text-xl flex items-center gap-2">
                            <FileText className="w-5 h-5" />
                            Export Journal
                            {!canExportPdf && (
                                <span className="px-2 py-0.5 text-xs rounded-full bg-primary/10 text-primary">
                                    Plus
                                </span>
                            )}
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <p className="text-sm text-muted-foreground">
                            Download your journal entries as a beautifully formatted PDF with mood stats, stickers, and custom colors.
                        </p>
                        <div className="flex flex-wrap gap-2">
                            <Button
                                variant={canExportPdf ? "default" : "outline"}
                                onClick={() => handleExportPdf(7)}
                                disabled={!canExportPdf || exportingPdf}
                                className="rounded-full"
                                data-testid="export-pdf-7"
                            >
                                {exportingPdf ? (
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                ) : (
                                    <Download className="w-4 h-4 mr-2" />
                                )}
                                Last 7 Days
                            </Button>
                            <Button
                                variant={canExportPdf ? "default" : "outline"}
                                onClick={() => handleExportPdf(30)}
                                disabled={!canExportPdf || exportingPdf}
                                className="rounded-full"
                                data-testid="export-pdf-30"
                            >
                                {exportingPdf ? (
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                ) : (
                                    <Download className="w-4 h-4 mr-2" />
                                )}
                                Last 30 Days
                            </Button>
                            <Button
                                variant={canExportPdf ? "default" : "outline"}
                                onClick={() => handleExportPdf(365)}
                                disabled={!canExportPdf || exportingPdf}
                                className="rounded-full"
                                data-testid="export-pdf-365"
                            >
                                {exportingPdf ? (
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                ) : (
                                    <Download className="w-4 h-4 mr-2" />
                                )}
                                Last Year
                            </Button>
                        </div>
                        {!canExportPdf && (
                            <Button
                                variant="outline"
                                onClick={() => window.location.href = '/pricing'}
                                className="rounded-full w-full"
                            >
                                <Crown className="w-4 h-4 mr-2" />
                                Upgrade to Plus to Export
                            </Button>
                        )}
                    </CardContent>
                </Card>

                {/* Theme Section */}
                <Card className="glass-card">
                    <CardHeader>
                        <CardTitle className="font-heading text-xl flex items-center gap-2">
                            <Palette className="w-5 h-5" />
                            Appearance
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-6">
                        <div className="space-y-3">
                            <Label>App Theme</Label>
                            <div className="flex gap-3">
                                {themeOptions.map((option) => (
                                    <Button
                                        key={option.value}
                                        variant={theme === option.value ? "default" : "outline"}
                                        onClick={() => setTheme(option.value)}
                                        className="rounded-full flex-1"
                                        data-testid={`theme-${option.value}`}
                                    >
                                        <option.icon className="w-4 h-4 mr-2" />
                                        {option.label}
                                    </Button>
                                ))}
                            </div>
                            <p className="text-sm text-muted-foreground">
                                System theme follows your device settings
                            </p>
                        </div>
                    </CardContent>
                </Card>

                {/* Journal Customization Section */}
                <Card className="glass-card">
                    <CardHeader>
                        <CardTitle className="font-heading text-xl flex items-center gap-2">
                            <Sparkles className="w-5 h-5" />
                            Journal Customization
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-6">
                        {/* Theme Presets */}
                        <div className="space-y-3">
                            <Label className="text-sm font-medium">Quick Theme Presets</Label>
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                                {[
                                    { name: 'Default', bg: '#ffffff', text: '#1a1a1a', accent: '#7c3aed' },
                                    { name: 'Ocean Calm', bg: '#e0f2fe', text: '#0c4a6e', accent: '#0284c7' },
                                    { name: 'Forest', bg: '#dcfce7', text: '#14532d', accent: '#16a34a' },
                                    { name: 'Sunset', bg: '#fef3c7', text: '#78350f', accent: '#d97706' },
                                    { name: 'Lavender', bg: '#f3e8ff', text: '#581c87', accent: '#9333ea' },
                                    { name: 'Rose', bg: '#ffe4e6', text: '#881337', accent: '#e11d48' },
                                    { name: 'Midnight', bg: '#1e293b', text: '#f1f5f9', accent: '#60a5fa' },
                                    { name: 'Serenity', bg: '#f0fdfa', text: '#134e4a', accent: '#14b8a6' },
                                ].map((preset) => (
                                    <button
                                        key={preset.name}
                                        onClick={() => setPreferences({
                                            ...preferences,
                                            journal_background: preset.bg,
                                            text_color: preset.text,
                                            accent_color: preset.accent
                                        })}
                                        className={`relative p-3 rounded-xl border-2 transition-all hover:scale-105 ${
                                            preferences.journal_background === preset.bg && preferences.text_color === preset.text
                                                ? 'border-primary ring-2 ring-primary/20'
                                                : 'border-border hover:border-primary/50'
                                        }`}
                                        style={{ backgroundColor: preset.bg }}
                                        data-testid={`preset-${preset.name.toLowerCase().replace(' ', '-')}`}
                                    >
                                        {preferences.journal_background === preset.bg && preferences.text_color === preset.text && (
                                            <div className="absolute -top-2 -right-2 w-5 h-5 bg-primary rounded-full flex items-center justify-center">
                                                <Check className="w-3 h-3 text-primary-foreground" />
                                            </div>
                                        )}
                                        <span className="text-xs font-medium" style={{ color: preset.text }}>
                                            {preset.name}
                                        </span>
                                        <div className="mt-1 h-1 w-full rounded" style={{ backgroundColor: preset.accent }} />
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Custom Colors */}
                        <div className="space-y-3">
                            <Label className="text-sm font-medium">Custom Colors</Label>
                            <div className="grid grid-cols-3 gap-4">
                                <div className="space-y-2">
                                    <Label className="text-xs text-muted-foreground">Background</Label>
                                    <Input
                                        type="color"
                                        value={preferences.journal_background || '#ffffff'}
                                        onChange={(e) => setPreferences({
                                            ...preferences,
                                            journal_background: e.target.value
                                        })}
                                        className="h-12 rounded-xl cursor-pointer"
                                        data-testid="custom-bg-color"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label className="text-xs text-muted-foreground">Text</Label>
                                    <Input
                                        type="color"
                                        value={preferences.text_color || '#000000'}
                                        onChange={(e) => setPreferences({
                                            ...preferences,
                                            text_color: e.target.value
                                        })}
                                        className="h-12 rounded-xl cursor-pointer"
                                        data-testid="custom-text-color"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label className="text-xs text-muted-foreground">Accent</Label>
                                    <Input
                                        type="color"
                                        value={preferences.accent_color || '#7c3aed'}
                                        onChange={(e) => setPreferences({
                                            ...preferences,
                                            accent_color: e.target.value
                                        })}
                                        className="h-12 rounded-xl cursor-pointer"
                                        data-testid="custom-accent-color"
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Font Selection */}
                        <div className="space-y-3">
                            <Label className="text-sm font-medium">Journal Font</Label>
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                                {[
                                    { name: 'Default', value: 'inherit', sample: 'Aa' },
                                    { name: 'Serif', value: 'Georgia, serif', sample: 'Aa' },
                                    { name: 'Mono', value: 'ui-monospace, monospace', sample: 'Aa' },
                                    { name: 'Rounded', value: 'system-ui, sans-serif', sample: 'Aa' },
                                ].map((font) => (
                                    <button
                                        key={font.name}
                                        onClick={() => setPreferences({
                                            ...preferences,
                                            font_family: font.value
                                        })}
                                        className={`p-3 rounded-xl border-2 transition-all ${
                                            (preferences.font_family || 'inherit') === font.value
                                                ? 'border-primary bg-primary/5'
                                                : 'border-border hover:border-primary/50'
                                        }`}
                                        data-testid={`font-${font.name.toLowerCase()}`}
                                    >
                                        <span className="text-2xl font-medium" style={{ fontFamily: font.value }}>
                                            {font.sample}
                                        </span>
                                        <p className="text-xs text-muted-foreground mt-1">{font.name}</p>
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Preview Card */}
                        <div className="space-y-3">
                            <Label className="text-sm font-medium">Preview</Label>
                            <div 
                                className="p-4 rounded-xl border border-border"
                                style={{ 
                                    backgroundColor: preferences.journal_background || '#ffffff',
                                    color: preferences.text_color || '#000000',
                                    fontFamily: preferences.font_family || 'inherit'
                                }}
                            >
                                <div className="flex items-center gap-2 mb-2">
                                    <span className="text-xl">😊</span>
                                    <span className="font-medium" style={{ color: preferences.accent_color || '#7c3aed' }}>
                                        Mood: 8/10
                                    </span>
                                </div>
                                <p className="text-sm opacity-90">
                                    This is how your journal entries will look with your custom theme. 
                                    Express yourself freely and make your journal uniquely yours.
                                </p>
                                <p className="text-xs mt-2 opacity-60">📍 New York, USA • 🎵 Lo-fi beats</p>
                            </div>
                        </div>

                        <Button
                            onClick={handleSavePreferences}
                            disabled={saving}
                            className="rounded-full w-full"
                            data-testid="save-preferences-btn"
                        >
                            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                            Save Journal Preferences
                        </Button>
                    </CardContent>
                </Card>

                {/* Notifications Section */}
                <Card className="glass-card">
                    <CardHeader>
                        <CardTitle className="font-heading text-xl flex items-center gap-2">
                            <Bell className="w-5 h-5" />
                            Notifications
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        {/* Email Notifications */}
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="font-medium flex items-center gap-2">
                                    <Mail className="w-4 h-4" />
                                    Email Notifications
                                </p>
                                <p className="text-sm text-muted-foreground">
                                    Receive reminders and journal prompts via email
                                </p>
                            </div>
                            <Switch
                                checked={receiveEmails}
                                onCheckedChange={handleEmailToggle}
                                disabled={saving}
                                data-testid="email-toggle"
                            />
                        </div>
                    </CardContent>
                </Card>

                {/* Subscription Status */}
                <Card className="glass-card">
                    <CardHeader>
                        <CardTitle className="font-heading text-xl flex items-center gap-2">
                            <Crown className="w-5 h-5" />
                            Subscription
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="font-medium">Current Plan</p>
                                <p className="text-sm text-muted-foreground capitalize">
                                    MoodTrack {user?.subscription?.tier || 'Basic'}
                                </p>
                            </div>
                            {user?.subscription?.tier !== 'complete' && (
                                <Button
                                    variant="outline"
                                    onClick={() => window.location.href = '/pricing'}
                                    className="rounded-full"
                                    data-testid="upgrade-btn"
                                >
                                    <Crown className="w-4 h-4 mr-2" />
                                    Upgrade
                                </Button>
                            )}
                        </div>
                        {user?.mood_streak > 0 && (
                            <div className="pt-4 border-t border-border/50">
                                <p className="text-sm text-muted-foreground">
                                    Current streak: <span className="font-medium text-foreground">{user.mood_streak} day{user.mood_streak !== 1 ? 's' : ''}</span>
                                </p>
                            </div>
                        )}
                    </CardContent>
                </Card>

                {/* Logout */}
                <Card className="glass-card border-destructive/20">
                    <CardContent className="p-6">
                        <Button
                            variant="outline"
                            onClick={handleLogout}
                            className="rounded-full text-destructive hover:bg-destructive/10 w-full"
                            data-testid="logout-btn"
                        >
                            <LogOut className="w-4 h-4 mr-2" />
                            Sign Out
                        </Button>
                    </CardContent>
                </Card>
            </div>
        </DashboardLayout>
    );
};

export default SettingsPage;
